import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LockIcon, CheckCircleIcon, TrendingUpIcon, SearchIcon, UsersIcon, HomeIcon, EyeIcon, FileTextIcon, DollarSignIcon, TargetIcon, AwardIcon, SparklesIcon, ArrowRightIcon, CrownIcon, ShieldCheckIcon, ZapIcon, TrophyIcon, RocketIcon, PlayCircleIcon, UserCheckIcon, ClockIcon, StarIcon, BriefcaseIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useProgress } from '../hooks/useProgress';
export function SaleModuleLocked() {
  const {
    user
  } = useAuth();
  const {
    getCourseProgress
  } = useProgress();
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  // Check progress for all 4 steps
  const course1Progress = getCourseProgress('concept-clc', 6);
  const hasCompletedCourse1 = course1Progress === 100;
  const hasCompletedOnboarding = user ? !!(user.fullName && user.phone && user.dateOfBirth && user.city && user.address) : false;
  const course2Progress = getCourseProgress('proceduri-inchiriere', 18);
  const hasCompletedCourse2 = course2Progress === 100;
  const course3Progress = getCourseProgress('proceduri-vanzare', 20);
  const hasCompletedCourse3 = course3Progress === 100;
  // Calculate overall progress (4 steps, each worth 25%)
  const step1Contribution = course1Progress / 100 * 25;
  const step2Contribution = hasCompletedOnboarding ? 25 : 0;
  const step3Contribution = course2Progress / 100 * 25;
  const step4Contribution = course3Progress / 100 * 25;
  const progressPercentage = Math.round(step1Contribution + step2Contribution + step3Contribution + step4Contribution);
  const completedSteps = [hasCompletedCourse1, hasCompletedOnboarding, hasCompletedCourse2, hasCompletedCourse3].filter(Boolean).length;
  // Determine next action
  const getNextAction = () => {
    if (!hasCompletedCourse1) {
      return {
        text: 'Continuă Training 1',
        path: '/cursuri/concept-clc',
        icon: PlayCircleIcon
      };
    }
    if (!hasCompletedOnboarding) {
      return {
        text: 'Finalizează On-boarding-ul',
        path: '/onboarding',
        icon: UserCheckIcon
      };
    }
    if (!hasCompletedCourse2) {
      return {
        text: 'Începe Training 2',
        path: '/cursuri/proceduri-inchiriere',
        icon: PlayCircleIcon
      };
    }
    if (!hasCompletedCourse3) {
      return {
        text: 'Începe Training 3',
        path: '/cursuri/proceduri-vanzare',
        icon: PlayCircleIcon
      };
    }
    return {
      text: 'Acces Deblocat!',
      path: '/vanzare',
      icon: RocketIcon
    };
  };
  const nextAction = getNextAction();
  // Premium features for sales module
  const premiumFeatures = [{
    title: 'Evaluare Proprietăți',
    description: 'Analiză completă de piață + comparații',
    icon: SearchIcon,
    gradient: 'from-emerald-600 via-teal-600 to-cyan-600',
    stats: 'Evaluare precisă',
    highlight: 'Premium'
  }, {
    title: 'Matching Cumpărători',
    description: 'Potrivire inteligentă între cumpărători și proprietăți',
    icon: UsersIcon,
    gradient: 'from-blue-700 via-indigo-700 to-purple-700',
    stats: 'AI-powered',
    highlight: 'Inteligent'
  }, {
    title: 'Portofoliu Proprietari Premium',
    description: 'Acces la proprietari reali interesați de vânzare',
    icon: HomeIcon,
    gradient: 'from-amber-600 via-orange-600 to-red-600',
    stats: 'Verificați',
    highlight: 'Exclusiv'
  }, {
    title: 'Contracte Vânzare PF/PJ',
    description: 'Generare automată + semnare digitală',
    icon: FileTextIcon,
    gradient: 'from-purple-700 via-violet-700 to-fuchsia-700',
    stats: 'Legal complet',
    highlight: 'Securizat'
  }, {
    title: 'Vizionări Vânzări',
    description: 'Organizare completă a vizionărilor',
    icon: EyeIcon,
    gradient: 'from-cyan-700 via-blue-700 to-indigo-700',
    stats: 'Programare auto',
    highlight: 'Eficient'
  }, {
    title: 'Pipeline Tranzacții Mari',
    description: 'Etapele tranzacției vizibile până la încasarea comisionului',
    icon: TrendingUpIcon,
    gradient: 'from-emerald-700 via-green-700 to-teal-700',
    stats: 'Transparență',
    highlight: 'Control total'
  }];
  // Premium benefits
  const premiumBenefits = [{
    icon: DollarSignIcon,
    title: 'Comisioane Mari',
    description: 'Minim 3.500€ pe tranzacție',
    color: 'from-emerald-500 to-teal-600',
    stat: '3.500€+'
  }, {
    icon: CrownIcon,
    title: 'Clienți Premium',
    description: 'Proprietari și cumpărători serioși',
    color: 'from-amber-500 to-orange-600',
    stat: 'Nivel superior'
  }, {
    icon: TrophyIcon,
    title: 'Evoluție Profesională',
    description: 'Nivel avansat în cariera ta',
    color: 'from-blue-600 to-indigo-700',
    stat: 'Agent de top'
  }];
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50" data-id="element-11547">
      {/* Hero Section - Premium Dark - Mobile Optimized */}
      <section className="relative bg-gradient-to-br from-[#132C53] to-[#1e4278] text-white pt-20 pb-12 sm:pt-24 sm:pb-16 md:pt-32 md:pb-24 overflow-hidden" data-id="element-11548">
        {/* Background pattern overlay */}
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-11549"></div>

        {/* Premium gold accent lines */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-amber-400 to-transparent opacity-50" data-id="element-11550"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-emerald-400 to-transparent opacity-50" data-id="element-11551"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-11552">
          <div className="text-center max-w-4xl mx-auto" data-id="element-11553">
            {/* Premium Lock Badge */}
            <motion.div initial={{
            opacity: 0,
            scale: 0
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.2,
            type: 'spring',
            stiffness: 200
          }} className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full mb-4 sm:mb-6 border border-white/20" data-id="element-11554">
              <LockIcon className="w-4 h-4 text-yellow-400" data-id="element-11555" />
              <span className="text-xs sm:text-sm font-medium text-white" data-id="element-11556">
                Modul temporar blocat
              </span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight mb-4 sm:mb-6 text-white leading-tight px-2 sm:px-0" data-id="element-11557">
              <span className="block mb-2" data-id="element-11558">Modul Vânzare</span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#357BEB] to-[#21A366]" data-id="element-11559">
                Așteaptă să fie deblocat
              </span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.4
          }} className="text-base sm:text-lg md:text-2xl text-gray-300 mb-6 sm:mb-10 max-w-2xl mx-auto leading-relaxed px-4 sm:px-0" data-id="element-11560">
              Acest modul este disponibil doar agenților pregătiți și
              certificați, după finalizarea completă a trainingului.
            </motion.p>

            {/* Premium Status Card - Mobile Optimized */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.6
          }} className="inline-flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 bg-white/10 backdrop-blur-xl rounded-2xl px-4 sm:px-8 py-4 border border-white/20 w-full sm:w-auto max-w-md sm:max-w-none mx-auto" data-id="element-11561">
              <div className="flex items-center space-x-2" data-id="element-11562">
                <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse" data-id="element-11563" />
                <span className="text-white font-semibold text-sm sm:text-base" data-id="element-11564">
                  Status:
                </span>
              </div>
              <div className="px-3 sm:px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl w-full sm:w-auto text-center" data-id="element-11565">
                <span className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-11566">
                  Trainee - Lipsă On-boarding
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Decorative blur elements */}
        <div className="absolute -top-10 -right-10 w-40 h-40 sm:w-64 sm:h-64 bg-[#357BEB] rounded-full blur-3xl opacity-20" data-id="element-11567"></div>
        <div className="absolute -bottom-10 -left-10 w-40 h-40 sm:w-64 sm:h-64 bg-[#21A366] rounded-full blur-3xl opacity-20" data-id="element-11568"></div>
      </section>

      {/* 4-Step Progress Timeline - Mobile Optimized */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 sm:-mt-16 mb-12 sm:mb-20" data-id="element-11569">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.8
      }} className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 shadow-2xl border-2 border-emerald-200" data-id="element-11570">
          <div className="text-center mb-8 sm:mb-12" data-id="element-11571">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-11572">
              Cum Deblochezi Modulul de Vânzare
            </h2>
            <p className="text-gray-600 text-sm sm:text-base md:text-lg" data-id="element-11573">
              4 pași necesari pentru accesul la modulul premium
            </p>
          </div>

          {/* Progress bar */}
          <div className="mb-8 sm:mb-12" data-id="element-11574">
            <div className="flex justify-between items-center mb-3 sm:mb-4" data-id="element-11575">
              <span className="text-slate-900 font-semibold text-sm sm:text-base" data-id="element-11576">
                Progres total
              </span>
              <span className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent" data-id="element-11577">
                {progressPercentage}%
              </span>
            </div>
            <div className="relative h-3 sm:h-4 bg-gray-200 rounded-full overflow-hidden" data-id="element-11578">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${progressPercentage}%`
            }} transition={{
              duration: 1.5,
              delay: 1,
              ease: 'easeOut'
            }} className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-full" data-id="element-11579" />
            </div>
          </div>

          {/* 4 Steps Grid - Mobile: 1 col, Tablet: 2 cols, Desktop: 4 cols */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-id="element-11580">
            {/* Step 1 - Active by default */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1
          }} className="relative group" data-id="element-11581">
              <div className="relative rounded-xl sm:rounded-2xl p-5 sm:p-6 transition-all duration-300 bg-gradient-to-br from-[#357BEB] to-[#2868d8] text-white ring-4 ring-[#357BEB] ring-opacity-50 min-h-[240px] sm:min-h-[280px] flex flex-col" data-id="element-11582">
                <div className="flex items-center justify-between mb-4" data-id="element-11583">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center bg-white/20 backdrop-blur-sm" data-id="element-11584">
                    <span className="text-xl sm:text-2xl font-bold text-white" data-id="element-11585">
                      1
                    </span>
                  </div>
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-white" data-id="element-11586">
                  Step 1 - Training 1
                </h3>
                <div className="mb-4" data-id="element-11587">
                  <div className="px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold bg-white/20" data-id="element-11588">
                    <span className="text-white" data-id="element-11589">În progres</span>
                  </div>
                </div>
                <Link to="/cursuri/concept-clc" className="inline-flex items-center space-x-2 text-white font-semibold hover:translate-x-1 transition-transform mt-auto text-sm sm:text-base min-h-[44px] justify-start" data-id="element-11590">
                  <span data-id="element-11591">Începe acum</span>
                  <ArrowRightIcon className="w-4 h-4" data-id="element-11592" />
                </Link>
              </div>
            </motion.div>

            {/* Step 2 - Locked */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1.2
          }} className="relative group" data-id="element-11593">
              <div className="relative rounded-xl sm:rounded-2xl p-5 sm:p-6 transition-all duration-300 bg-white border-2 border-gray-300 text-gray-900 min-h-[240px] sm:min-h-[280px] flex flex-col" data-id="element-11594">
                <div className="flex items-center justify-between mb-4" data-id="element-11595">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center bg-gray-100" data-id="element-11596">
                    <span className="text-xl sm:text-2xl font-bold text-gray-400" data-id="element-11597">
                      2
                    </span>
                  </div>
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-gray-900" data-id="element-11598">
                  Step 2 - Finalizează On-boarding
                </h3>
                <div className="mb-4" data-id="element-11599">
                  <div className="px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold bg-gray-100" data-id="element-11600">
                    <span className="text-gray-500" data-id="element-11601">
                      Finalizează Training 1
                    </span>
                  </div>
                </div>
                <div className="mt-auto h-[28px]" data-id="element-11602"></div>
              </div>
            </motion.div>

            {/* Step 3 - Locked */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1.4
          }} className="relative group" data-id="element-11603">
              <div className="relative rounded-xl sm:rounded-2xl p-5 sm:p-6 transition-all duration-300 bg-white border-2 border-gray-300 text-gray-900 min-h-[240px] sm:min-h-[280px] flex flex-col" data-id="element-11604">
                <div className="flex items-center justify-between mb-4" data-id="element-11605">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center bg-gray-100" data-id="element-11606">
                    <span className="text-xl sm:text-2xl font-bold text-gray-400" data-id="element-11607">
                      3
                    </span>
                  </div>
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-gray-900" data-id="element-11608">
                  Step 3 - Training 2
                </h3>
                <div className="mb-4" data-id="element-11609">
                  <div className="px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold bg-gray-100" data-id="element-11610">
                    <span className="text-gray-500" data-id="element-11611">
                      Finalizează Training 1 și On-boarding
                    </span>
                  </div>
                </div>
                <div className="mt-auto h-[28px]" data-id="element-11612"></div>
              </div>
            </motion.div>

            {/* Step 4 - Locked */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1.6
          }} className="relative group sm:col-span-2 lg:col-span-1" data-id="element-11613">
              <div className="relative rounded-xl sm:rounded-2xl p-5 sm:p-6 transition-all duration-300 bg-white border-2 border-gray-300 text-gray-900 min-h-[240px] sm:min-h-[280px] flex flex-col" data-id="element-11614">
                <div className="flex items-center justify-between mb-4" data-id="element-11615">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center bg-gray-100" data-id="element-11616">
                    <span className="text-xl sm:text-2xl font-bold text-gray-400" data-id="element-11617">
                      4
                    </span>
                  </div>
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-gray-900" data-id="element-11618">
                  Step 4 - Training 3
                </h3>
                <div className="mb-4" data-id="element-11619">
                  <div className="px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold bg-gray-100" data-id="element-11620">
                    <span className="text-gray-500" data-id="element-11621">
                      Finalizează Training 1, On-boarding și Training 2
                    </span>
                  </div>
                </div>
                <div className="mt-auto h-[28px]" data-id="element-11622"></div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Premium Features Grid - Mobile Optimized */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 sm:mb-20" data-id="element-11623">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.2
      }} className="text-center mb-8 sm:mb-12" data-id="element-11624">
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-100 to-orange-100 px-3 sm:px-4 py-2 rounded-full mb-3 sm:mb-4" data-id="element-11625">
            <CrownIcon className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" data-id="element-11626" />
            <span className="text-xs sm:text-sm font-bold text-amber-900" data-id="element-11627">
              Funcționalități Premium
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-3 sm:mb-4 px-4" data-id="element-11628">
            Funcționalitățile Premium Care Te Așteaptă
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4" data-id="element-11629">
            Modulul de Vânzare oferă instrumente avansate pentru tranzacții mari
            și comisioane de minimum 3.500€
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-11630">
          {premiumFeatures.map((feature, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.4 + index * 0.1
        }} onHoverStart={() => setHoveredFeature(index)} onHoverEnd={() => setHoveredFeature(null)} className="group relative" data-id="element-11631">
              <div className="relative h-full bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 border-2 border-gray-200 transition-all duration-300 hover:border-emerald-300 hover:shadow-xl" data-id="element-11632">
                {/* Premium Lock overlay */}
                <div className="absolute top-3 right-3 sm:top-4 sm:right-4 w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-amber-600 to-orange-600 rounded-full flex items-center justify-center shadow-lg" data-id="element-11633">
                  <LockIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" data-id="element-11634" />
                </div>

                {/* Icon */}
                <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`} data-id="element-11635">
                  <feature.icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" data-id="element-11636" />
                </div>

                {/* Content */}
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2" data-id="element-11637">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4" data-id="element-11638">
                  {feature.description}
                </p>

                {/* Stats - Mobile: Stack vertically, Desktop: Side by side */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2" data-id="element-11639">
                  <div className="px-3 py-1.5 bg-gray-100 rounded-lg" data-id="element-11640">
                    <span className="text-xs font-bold text-gray-700" data-id="element-11641">
                      {feature.stats}
                    </span>
                  </div>
                  <div className="px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-600 rounded-lg" data-id="element-11642">
                    <span className="text-xs font-bold text-white" data-id="element-11643">
                      {feature.highlight}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>

      {/* Premium Benefits - Mobile Optimized */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 sm:mb-20" data-id="element-11644">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.8
      }} className="text-center mb-8 sm:mb-12" data-id="element-11645">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4 px-4" data-id="element-11646">
            De Ce Merită să Deblochezi Modulul de Vânzare?
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-id="element-11647">
          {premiumBenefits.map((benefit, index) => <motion.div key={index} initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 2 + index * 0.1
        }} whileHover={{
          scale: 1.05,
          y: -5
        }} className="relative group" data-id="element-11648">
              <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-emerald-200 text-center hover:border-emerald-400 hover:shadow-2xl transition-all" data-id="element-11649">
                <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br ${benefit.color} flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:rotate-12 transition-transform shadow-lg`} data-id="element-11650">
                  <benefit.icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" data-id="element-11651" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 sm:mb-3" data-id="element-11652">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 mb-3 sm:mb-4 text-base sm:text-lg" data-id="element-11653">
                  {benefit.description}
                </p>
                <div className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-xl inline-block" data-id="element-11654">
                  <span className="text-sm sm:text-base font-bold text-emerald-900" data-id="element-11655">
                    {benefit.stat}
                  </span>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>

      {/* Final Premium CTA - Mobile Optimized */}
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-20" data-id="element-11656">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 2.2
      }} className="relative bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center overflow-hidden shadow-2xl" data-id="element-11657">
          {/* Premium pattern overlay */}
          <div className="absolute inset-0 opacity-10" data-id="element-11658">
            <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '30px 30px'
          }} data-id="element-11659" />
          </div>

          {/* Gold accent border */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-11660"></div>
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-11661"></div>

          <div className="relative z-10" data-id="element-11662">
            <motion.div animate={{
            rotate: [0, 360],
            scale: [1, 1.2, 1]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-11663">
              <CrownIcon className="w-8 h-8 sm:w-10 sm:h-10 text-amber-300" data-id="element-11664" />
            </motion.div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-4 px-4" data-id="element-11665">
              Ești Aproape de Accesul la Cel Mai Valoros Modul din CLC
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-white/90 mb-6 sm:mb-8 max-w-2xl mx-auto px-4" data-id="element-11666">
              Doar {4 - completedSteps}{' '}
              {4 - completedSteps === 1 ? 'pas' : 'pași'} te despart de
              comisioane de minimum 3.500€ pe tranzacție
            </p>

            <Link to={nextAction.path} className="inline-flex items-center justify-center space-x-2 sm:space-x-3 bg-white text-slate-900 px-6 sm:px-10 py-4 sm:py-5 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg hover:scale-105 transition-transform shadow-2xl group w-full sm:w-auto min-h-[56px]" data-id="element-11667">
              <nextAction.icon className="w-5 h-5 sm:w-6 sm:h-6 group-hover:rotate-12 transition-transform" data-id="element-11668" />
              <span data-id="element-11669">{nextAction.text}</span>
              <ArrowRightIcon className="w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-2 transition-transform" data-id="element-11670" />
            </Link>

            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-8 text-white/90" data-id="element-11671">
              <div className="flex items-center space-x-2" data-id="element-11672">
                <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-11673" />
                <span className="text-sm font-semibold" data-id="element-11674">Training complet</span>
              </div>
              <div className="flex items-center space-x-2" data-id="element-11675">
                <StarIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-11676" />
                <span className="text-sm font-semibold" data-id="element-11677">Nivel avansat</span>
              </div>
              <div className="flex items-center space-x-2" data-id="element-11678">
                <DollarSignIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-11679" />
                <span className="text-sm font-semibold" data-id="element-11680">3.500€+</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}